import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BackHandler,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Portal, Surface } from "react-native-paper";

import { useEscapeToDismiss } from "../hooks/useEscapeToDismiss";

interface HeaderMenuProps {
  visible: boolean;
  onDismiss: () => void;
  anchor: React.ReactNode;
  children: React.ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
}

const GUTTER = 8;
// Used until the anchor has been measured once (header height, right gutter).
const DEFAULT_POSITION = { top: 56, right: GUTTER };

// On web, host views are DOM elements, so focus can be moved with selectors.
const focusFirst = (node: View | null, selector: string) => {
  if (Platform.OS !== "web") return;
  (node as unknown as HTMLElement | null)
    ?.querySelector<HTMLElement>(selector)
    ?.focus();
};

/**
 * Dropdown anchored under the header's hamburger, opened purely by `visible`.
 *
 * Replaces react-native-paper's Menu, which closes itself on any Dimensions
 * change: on web its reopen frame overflowed the window, the scrollbar
 * resized the viewport, and the menu dismissed itself mid-open, leaving its
 * internal state stuck so later presses did nothing until a reload. This one
 * is right-aligned (it cannot overflow), ignores resizes, and fully unmounts
 * its overlay when closed.
 */
const HeaderMenu: React.FC<HeaderMenuProps> = ({
  visible,
  onDismiss,
  anchor,
  children,
  contentStyle,
}) => {
  const anchorRef = useRef<View>(null);
  const { width, height } = useWindowDimensions();
  const [position, setPosition] = useState(DEFAULT_POSITION);

  const measureAnchor = useCallback(() => {
    anchorRef.current?.measureInWindow((x, y, w, h) => {
      if (![x, y, w, h].every(Number.isFinite)) return;
      setPosition({
        top: y + h,
        right: Math.max(GUTTER, width - (x + w)),
      });
    });
  }, [width]);

  useEffect(() => {
    if (visible) measureAnchor();
  }, [visible, measureAnchor, height]);

  useEscapeToDismiss(visible, onDismiss);

  // Keyboard users land on the first item when the menu opens and back on
  // the hamburger when it closes. The panel mounts later, inside the Portal,
  // and Animated re-attaches its ref on re-renders, so focus it once per open.
  const focusedItem = useRef(false);
  const focusFirstItem = useCallback((panel: View | null) => {
    if (!panel || focusedItem.current) return;
    focusedItem.current = true;
    focusFirst(panel, '[role="menuitem"]');
  }, []);
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (wasVisible.current && !visible) {
      focusedItem.current = false;
      focusFirst(anchorRef.current, '[role="button"]');
    }
    wasVisible.current = visible;
  }, [visible]);

  useEffect(() => {
    // Android back button; BackHandler logs an error if used on web.
    if (!visible || Platform.OS === "web") return undefined;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      onDismiss();
      return true;
    });
    return () => sub.remove();
  }, [visible, onDismiss]);

  return (
    <>
      <View ref={anchorRef} collapsable={false} onLayout={measureAnchor}>
        {anchor}
      </View>
      {visible && (
        <Portal>
          <Pressable
            accessibilityLabel="Close menu"
            style={styles.overlay}
            onPress={onDismiss}
          />
          <Surface
            ref={focusFirstItem}
            testID="header-menu"
            accessibilityRole="menu"
            elevation={2}
            style={[
              styles.panel,
              { top: position.top, right: position.right },
              { maxWidth: width - 2 * GUTTER },
              contentStyle,
            ]}
          >
            {children}
          </Surface>
        </Portal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    // No pointer cursor on web: the overlay is an invisible click-catcher.
    cursor: "auto",
  } as ViewStyle,
  panel: {
    position: "absolute",
    paddingVertical: 8,
    borderRadius: 4,
  },
});

export default HeaderMenu;
